import React, { useContext, useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  CardTitle,
  Label,
  FormGroup,
  Form,
  Input,
  Row,
  Col,
} from "reactstrap";
import NotificationAlert from "react-notification-alert";
import rest from "../../API/api";
import { AthletesContext } from "../../context/AthletesContext";
import { UserLoginContext } from "../../context/UserLoginContext";
import { authenticatedFetch } from "../../services/authService";

function EditAthlete() {
  const [athletesState, setAthletesState] = useContext(AthletesContext);
  const [userState] = useContext(UserLoginContext);
  const history = useHistory();
  const notificationAlertRef = useRef(null);

  const [athlete, setAthlete] = useState({
    _id: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    dateOfBirth: "",
    gender: "",
    sport: "",
    position: "",
    teamName: "",
    jerseyNumber: "",
    height: "",
    weight: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    emergencyContactName: "",
    emergencyContactPhone: "",
    status: "Active",
    notes: ""
  });

  useEffect(() => {
    if (athletesState.theAthlete) {
      const dateStr = athletesState.theAthlete.dateOfBirth
        ? new Date(athletesState.theAthlete.dateOfBirth).toISOString().split('T')[0]
        : "";

      setAthlete({
        _id: athletesState.theAthlete._id || "",
        firstName: athletesState.theAthlete.firstName || "",
        lastName: athletesState.theAthlete.lastName || "",
        email: athletesState.theAthlete.email || "",
        phone: athletesState.theAthlete.phone || "",
        dateOfBirth: dateStr,
        gender: athletesState.theAthlete.gender || "",
        sport: athletesState.theAthlete.sport || "",
        position: athletesState.theAthlete.position || "",
        teamName: athletesState.theAthlete.teamName || "",
        jerseyNumber: athletesState.theAthlete.jerseyNumber || "",
        height: athletesState.theAthlete.height || "",
        weight: athletesState.theAthlete.weight || "",
        address: athletesState.theAthlete.address || "",
        city: athletesState.theAthlete.city || "",
        state: athletesState.theAthlete.state || "",
        zipCode: athletesState.theAthlete.zipCode || "",
        emergencyContactName: athletesState.theAthlete.emergencyContactName || "",
        emergencyContactPhone: athletesState.theAthlete.emergencyContactPhone || "",
        status: athletesState.theAthlete.status || "Active",
        notes: athletesState.theAthlete.notes || ""
      });
    }
  }, [athletesState.theAthlete]);

  // Phone number formatting function
  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const phoneNumber = value.replace(/\D/g, '');

    // Format based on length
    if (phoneNumber.length === 0) {
      return '';
    } else if (phoneNumber.length <= 3) {
      return `(${phoneNumber}`;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Apply phone formatting to phone fields
    if (name === 'phone' || name === 'emergencyContactPhone') {
      setAthlete(prev => ({ ...prev, [name]: formatPhoneNumber(value) }));
    } else {
      setAthlete(prev => ({ ...prev, [name]: value }));
    }
  };

  const notify = (type, message) => {
    const options = {
      place: "tc",
      message: <div>{message}</div>,
      type: type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    };
    notificationAlertRef.current.notificationAlert(options);
  };

  const updateAthlete = async () => {
    if (!athlete.firstName || !athlete.lastName) {
      notify("warning", "First Name and Last Name are required!");
      return;
    }

    try {
      const response = await authenticatedFetch(`${rest.athletes}/${athlete._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(athlete)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Update failed:', errorData);
        throw new Error(errorData.message || 'Failed to update athlete');
      }

      const athletesData = await response.json();
      setAthletesState(prev => ({
        ...prev,
        theAthletes: athletesData
      }));

      notify("success", "Athlete has been updated successfully!");
      setTimeout(() => {
        history.push("/admin/athletes");
      }, 1000);
    } catch (error) {
      console.error('Error updating athlete:', error);
      notify("danger", error.message || 'Failed to update athlete.');
    }
  };

  return (
    <>
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      <div className="content">
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <CardTitle tag="h4">Edit Athlete</CardTitle>
              </CardHeader>
              <CardBody>
                <Form>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label>First Name *</Label>
                        <Input
                          name="firstName"
                          value={athlete.firstName}
                          onChange={handleChange}
                          placeholder="First Name"
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>Last Name *</Label>
                        <Input
                          name="lastName"
                          value={athlete.lastName}
                          onChange={handleChange}
                          placeholder="Last Name"
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label>Email</Label>
                        <Input
                          name="email"
                          value={athlete.email}
                          onChange={handleChange}
                          placeholder="Email"
                          type="email"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>Phone</Label>
                        <Input
                          name="phone"
                          value={athlete.phone}
                          onChange={handleChange}
                          placeholder="(555) 555-5555"
                          type="tel"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="4">
                      <FormGroup>
                        <Label>Date of Birth</Label>
                        <Input
                          name="dateOfBirth"
                          value={athlete.dateOfBirth}
                          onChange={handleChange}
                          type="date"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label>Gender</Label>
                        <Input
                          name="gender"
                          value={athlete.gender}
                          onChange={handleChange}
                          type="select"
                        >
                          <option value="">Select...</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label>Status</Label>
                        <Input
                          name="status"
                          value={athlete.status}
                          onChange={handleChange}
                          type="select"
                        >
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                          <option value="Injured">Injured</option>
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="4">
                      <FormGroup>
                        <Label>Sport</Label>
                        <Input
                          name="sport"
                          value={athlete.sport}
                          onChange={handleChange}
                          placeholder="Sport"
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label>Position</Label>
                        <Input
                          name="position"
                          value={athlete.position}
                          onChange={handleChange}
                          placeholder="Position"
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label>Team Name</Label>
                        <Input
                          name="teamName"
                          value={athlete.teamName}
                          onChange={handleChange}
                          placeholder="Team Name"
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="4">
                      <FormGroup>
                        <Label>Jersey Number</Label>
                        <Input
                          name="jerseyNumber"
                          value={athlete.jerseyNumber}
                          onChange={handleChange}
                          placeholder="Jersey Number"
                          type="number"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label>Height</Label>
                        <Input
                          name="height"
                          value={athlete.height}
                          onChange={handleChange}
                          placeholder="e.g., 6'2&quot;"
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label>Weight (lbs)</Label>
                        <Input
                          name="weight"
                          value={athlete.weight}
                          onChange={handleChange}
                          placeholder="Weight"
                          type="number"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="12">
                      <FormGroup>
                        <Label>Address</Label>
                        <Input
                          name="address"
                          value={athlete.address}
                          onChange={handleChange}
                          placeholder="Address"
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="4">
                      <FormGroup>
                        <Label>City</Label>
                        <Input
                          name="city"
                          value={athlete.city}
                          onChange={handleChange}
                          placeholder="City"
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label>State</Label>
                        <Input
                          name="state"
                          value={athlete.state}
                          onChange={handleChange}
                          placeholder="State"
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label>Zip Code</Label>
                        <Input
                          name="zipCode"
                          value={athlete.zipCode}
                          onChange={handleChange}
                          placeholder="Zip Code"
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label>Emergency Contact Name</Label>
                        <Input
                          name="emergencyContactName"
                          value={athlete.emergencyContactName}
                          onChange={handleChange}
                          placeholder="Emergency Contact Name"
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>Emergency Contact Phone</Label>
                        <Input
                          name="emergencyContactPhone"
                          value={athlete.emergencyContactPhone}
                          onChange={handleChange}
                          placeholder="(555) 555-5555"
                          type="tel"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="12">
                      <FormGroup>
                        <Label>Notes</Label>
                        <Input
                          name="notes"
                          value={athlete.notes}
                          onChange={handleChange}
                          placeholder="Additional notes..."
                          type="textarea"
                          rows="3"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
              <CardFooter>
                <Button className="btn-fill" color="info" onClick={updateAthlete}>
                  Update
                </Button>
                <Button color="secondary" onClick={() => history.push("/admin/athletes")}>
                  Cancel
                </Button>
              </CardFooter>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
}

export default EditAthlete;
